// src/pages/messages-ai/messageTypes.js
// Defines each message type: icon, label, description, and form fields.

export const MESSAGE_TYPES = [
  {
    id: 'Game Recap',
    icon: '🏅',
    label: 'Game Recap',
    description: 'Post-game summary with score and highlights',
    color: 'blue',
    fields: [
      { name: 'opponent',     label: 'Opponent team',        type: 'text',     placeholder: 'FC United', required: true },
      { name: 'ourScore',     label: 'Our score',            type: 'number',   placeholder: '2',         required: true, half: true },
      { name: 'theirScore',   label: 'Their score',          type: 'number',   placeholder: '1',         required: true, half: true },
      { name: 'date',         label: 'Game date',            type: 'date',     required: true },
      { name: 'highlights',   label: 'Key highlights',       type: 'textarea', placeholder: 'Great first half, strong defense all game…', required: true },
      { name: 'shoutouts',    label: 'Player shoutouts',     type: 'text',     placeholder: 'Alex, Maria, and Jordan for outstanding play' },
      { name: 'nextEvent',    label: 'Next practice/game',   type: 'text',     placeholder: 'Practice Thursday 6pm at Riverside Park' },
    ],
  },
  {
    id: 'Schedule Change',
    icon: '📍',
    label: 'Schedule Change',
    description: 'New time, location, or date for an event',
    color: 'orange',
    fields: [
      { name: 'eventType',        label: 'Event type',       type: 'select',   options: ['Practice', 'Game', 'Tournament', 'Meeting'], required: true },
      { name: 'originalDateTime', label: 'Original date/time', type: 'text',  placeholder: 'Saturday, June 7 at 10am', required: true },
      { name: 'newDateTime',      label: 'New date/time',    type: 'text',     placeholder: 'Sunday, June 8 at 2pm', required: true },
      { name: 'newLocation',      label: 'New location',     type: 'text',     placeholder: 'Central Park Field 3 (if changed)' },
      { name: 'reason',           label: 'Reason (optional)', type: 'text',    placeholder: 'Field unavailable due to maintenance' },
    ],
  },
  {
    id: 'Reminder',
    icon: '🔔',
    label: 'Reminder',
    description: 'Practice, game, or event reminder',
    color: 'green',
    fields: [
      { name: 'eventType',   label: 'Event type',     type: 'select',   options: ['Practice', 'Game', 'Tournament', 'Team Meeting', 'Other'], required: true },
      { name: 'date',        label: 'Date',           type: 'date',     required: true },
      { name: 'time',        label: 'Time',           type: 'time',     required: true },
      { name: 'location',    label: 'Location',       type: 'text',     placeholder: 'Riverside Park, Field 2', required: true },
      { name: 'whatToBring', label: 'What to bring',  type: 'text',     placeholder: 'Cleats, shin guards, water bottle, jersey' },
      { name: 'notes',       label: 'Extra notes',    type: 'textarea', placeholder: 'Any other info for parents…' },
    ],
  },
  {
    id: 'Payment Due',
    icon: '💰',
    label: 'Payment Due',
    description: 'Fees, registration, or equipment costs',
    color: 'yellow',
    fields: [
      { name: 'purpose',       label: "What it's for",  type: 'text',   placeholder: 'Spring season registration', required: true },
      { name: 'amount',        label: 'Amount ($)',      type: 'number', placeholder: '150',                        required: true },
      { name: 'dueDate',       label: 'Due date',        type: 'date',   required: true },
      { name: 'paymentMethod', label: 'How to pay',      type: 'text',   placeholder: 'Venmo @coach-name or cash at next practice', required: true },
      { name: 'contact',       label: 'Contact for questions', type: 'text', placeholder: 'coach@email.com or (555) 000-0000' },
    ],
  },
  {
    id: 'Cancellation',
    icon: '🚫',
    label: 'Cancellation',
    description: 'Practice or game called off',
    color: 'red',
    fields: [
      { name: 'eventType',        label: 'Event type',            type: 'select', options: ['Practice', 'Game', 'Tournament', 'Meeting'], required: true },
      { name: 'originalDateTime', label: 'Original date/time',    type: 'text',   placeholder: 'Saturday June 7 at 10am', required: true },
      { name: 'reason',           label: 'Reason',                type: 'select', options: ['Weather', 'Field unavailable', 'Low attendance', 'Holiday', 'Emergency', 'Other'], required: true },
      { name: 'nextEvent',        label: 'Next scheduled event',  type: 'text',   placeholder: 'Practice Tuesday 5pm — same location' },
    ],
  },
  {
    id: 'Motivation',
    icon: '🔥',
    label: 'Motivation',
    description: 'Pre-game hype, season kickoff, or team spirit',
    color: 'purple',
    fields: [
      { name: 'occasion',      label: 'Occasion',                 type: 'select',   options: ['Pre-game', 'Season Kickoff', 'After a tough loss', 'Playoffs', 'Mid-season boost', 'Championship'], required: true },
      { name: 'customMessage', label: 'Message from you (optional)', type: 'textarea', placeholder: "This team has worked so hard. I'm proud of every one of them…" },
    ],
  },
  {
    id: 'Tryout Info',
    icon: '📝',
    label: 'Tryout Info',
    description: 'Announce tryout dates, location, and details',
    color: 'teal',
    fields: [
      { name: 'dates',       label: 'Tryout dates',        type: 'text',   placeholder: 'July 12–14, 2025', required: true },
      { name: 'location',    label: 'Location',            type: 'text',   placeholder: 'Riverside Park, Field 2', required: true },
      { name: 'ageGroups',   label: 'Age groups',          type: 'text',   placeholder: 'U10, U12, U14', required: true },
      { name: 'whatToBring', label: 'What to bring',       type: 'text',   placeholder: 'Cleats, shin guards, water, registration form' },
      { name: 'contact',     label: 'Registration / contact', type: 'text', placeholder: 'coach@email.com or link to sign-up form' },
    ],
  },
  {
    id: 'Season Wrap',
    icon: '🎉',
    label: 'Season Wrap',
    description: 'End-of-season thank you and recap',
    color: 'emerald',
    fields: [
      { name: 'season',          label: 'Season',                  type: 'text',     placeholder: 'Spring 2025', required: true },
      { name: 'accomplishments', label: 'Key accomplishments',      type: 'textarea', placeholder: '8-2 record, tournament runners-up, all players improved…', required: true },
      { name: 'thankYou',        label: 'Personal thank you',       type: 'textarea', placeholder: 'Thank the parents, players, volunteers…' },
      { name: 'nextSeason',      label: 'Next season info',         type: 'text',     placeholder: 'Fall tryouts start in August — details coming soon' },
    ],
  },
];

export const TYPE_COLORS = {
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    icon: 'bg-blue-100 text-blue-600',    badge: 'bg-blue-100 text-blue-700' },
  orange:  { bg: 'bg-orange-50',  border: 'border-orange-200',  icon: 'bg-orange-100 text-orange-600', badge: 'bg-orange-100 text-orange-700' },
  green:   { bg: 'bg-green-50',   border: 'border-green-200',   icon: 'bg-green-100 text-green-600',   badge: 'bg-green-100 text-green-700' },
  yellow:  { bg: 'bg-yellow-50',  border: 'border-yellow-200',  icon: 'bg-yellow-100 text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' },
  red:     { bg: 'bg-red-50',     border: 'border-red-200',     icon: 'bg-red-100 text-red-600',       badge: 'bg-red-100 text-red-700' },
  purple:  { bg: 'bg-purple-50',  border: 'border-purple-200',  icon: 'bg-purple-100 text-purple-600', badge: 'bg-purple-100 text-purple-700' },
  teal:    { bg: 'bg-teal-50',    border: 'border-teal-200',    icon: 'bg-teal-100 text-teal-600',     badge: 'bg-teal-100 text-teal-700' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'bg-emerald-100 text-emerald-600', badge: 'bg-emerald-100 text-emerald-700' },
};
